import { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Compass, Briefcase, User, ChevronLeft, MessageSquare, MessagesSquare, Shield, Lightbulb } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUIStore, SIDEBAR_LIMITS } from "@/stores/useUIStore";
import { useAuth } from "@/hooks/useAuth";
import { useAgentRun } from "@/hooks/useAgentRun";
import { useSessionTaskCounts } from "@/hooks/useSessionTaskCounts";
import { useUnreadCounts } from "@/hooks/useUnreadCounts";
import { useDebugGesture } from "@/hooks/useDebugGesture";
import { SessionList } from "@/features/agent/SessionList";
import { FEEDBACK_URL, ROUTES } from "@/lib/constants";
import { BrandMark } from "@/components/ui/BrandMark";
import { isNavItemActive } from "./navActive";

const NAV_ITEMS = [
  { to: ROUTES.ASSISTANT, icon: MessageSquare, labelKey: "nav.assistant" },
  { to: ROUTES.CHATS, icon: MessagesSquare, labelKey: "nav.chats" },
  { to: ROUTES.DISCOVER, icon: Compass, labelKey: "nav.discover" },
  { to: ROUTES.WORK, icon: Briefcase, labelKey: "nav.myWork" },
  { to: ROUTES.PROFILE, icon: User, labelKey: "nav.profile" },
] as const;

function Sidebar() {
  const { t } = useTranslation();
  const { sidebarOpen, sidebarWidth, toggleSidebar, setSidebarWidth } = useUIStore();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const { runs, activeRunId, loadSession, deleteSession, newChat } =
    useAgentRun();
  const debugGesture = useDebugGesture();
  const taskCounts = useSessionTaskCounts(runs, activeRunId);
  const { unreadChats } = useUnreadCounts();
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; width: number } | null>(null);

  const isAssistantRoute = location.pathname.startsWith(ROUTES.ASSISTANT);

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!dragStartRef.current) return;
      const dx = e.clientX - dragStartRef.current.x;
      setSidebarWidth(dragStartRef.current.width + dx);
    },
    [setSidebarWidth],
  );

  const onPointerUp = useCallback(() => {
    dragStartRef.current = null;
    setIsDragging(false);
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, [onPointerMove]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!sidebarOpen) return;
    e.preventDefault();
    dragStartRef.current = { x: e.clientX, width: sidebarWidth };
    setIsDragging(true);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  // Cleanup any global listeners if the component unmounts mid-drag.
  useEffect(() => {
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  // Double-click the handle to reset width to default.
  const onHandleDoubleClick = () => {
    if (!sidebarOpen) return;
    setSidebarWidth(SIDEBAR_LIMITS.DEFAULT);
  };

  // Keyboard resize for accessibility (Left/Right arrows when handle focused).
  const onHandleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!sidebarOpen) return;
    const step = e.shiftKey ? 32 : 8;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setSidebarWidth(sidebarWidth - step);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setSidebarWidth(sidebarWidth + step);
    }
  };

  const widthStyle = sidebarOpen ? { width: `${sidebarWidth}px` } : undefined;

  return (
    <aside
      style={widthStyle}
      className={cn(
        "fixed left-0 top-0 z-30 hidden h-full border-r border-border-default bg-surface md:flex md:flex-col",
        !isDragging && "transition-[width] duration-200",
        !sidebarOpen && "w-16",
      )}
    >
      <div
        className={cn(
          "flex h-14 items-center justify-between border-b border-border-default",
          sidebarOpen ? "px-4" : "px-1.5",
        )}
      >
        <span
          onClick={debugGesture.onClick}
          className="flex select-none cursor-default items-center gap-2"
        >
          <BrandMark className={sidebarOpen ? "h-7 w-7" : "h-5 w-5"} />
          {sidebarOpen && (
            <span className="text-lg font-bold text-text-primary">weXare</span>
          )}
        </span>
        <button
          onClick={toggleSidebar}
          className="rounded-full p-1.5 text-text-muted hover:bg-surface-tertiary hover:text-text-secondary"
          aria-label={sidebarOpen ? t("sidebar.collapse") : t("sidebar.expand")}
        >
          <ChevronLeft
            className={cn(
              "h-4 w-4 transition-transform",
              !sidebarOpen && "rotate-180",
            )}
          />
        </button>
      </div>
      <nav className="mt-2 flex flex-col gap-1 px-2">
        {NAV_ITEMS.map((item) => {
          // A workspace chat sits under /assistant/w/… but belongs to Chats, so
          // activeness is ours, not NavLink's prefix match (NavLink would also
          // overwrite aria-current with its own answer).
          const active = isNavItemActive(item.to, location.pathname);
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex items-center gap-3 rounded-button px-3 py-2.5 text-sm font-medium transition-colors",
                active
                  ? "bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300"
                  : "text-text-secondary hover:bg-surface-secondary hover:text-text-primary",
                !sidebarOpen && "justify-center",
              )}
            >
              <span className="relative">
                <item.icon className="h-5 w-5 shrink-0" />
                {item.to === ROUTES.CHATS &&
                  unreadChats > 0 &&
                  !sidebarOpen && (
                    <span
                      aria-label={t("nav.unreadBadge", "{{count}} unread chats", {
                        count: unreadChats,
                      })}
                      className="absolute -top-1 -right-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-brand-500 px-0.5 text-[8px] font-bold text-white"
                    >
                      {unreadChats > 99 ? "99+" : unreadChats}
                    </span>
                  )}
              </span>
              {sidebarOpen && (
                <span className="flex-1 flex items-center justify-between">
                  <span>{t(item.labelKey)}</span>
                  {item.to === ROUTES.CHATS && unreadChats > 0 && (
                    <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-500 px-1 text-[10px] font-semibold text-white">
                      {unreadChats > 99 ? "99+" : unreadChats}
                    </span>
                  )}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Admin section */}
      {isAdmin && (
        <nav className="mt-2 border-t border-border-default px-2 pt-2">
          <NavLink
            to={ROUTES.ADMIN}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-button px-3 py-2.5 text-sm font-medium transition-colors",
                isActive
                  ? "bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300"
                  : "text-text-secondary hover:bg-surface-secondary hover:text-text-primary",
                !sidebarOpen && "justify-center",
              )
            }
          >
            <Shield className="h-5 w-5 shrink-0" />
            {sidebarOpen && <span>{t("nav.admin")}</span>}
          </NavLink>
        </nav>
      )}

      {/* Session history — only when on assistant route */}
      {isAssistantRoute && (
        <div className="mt-2 min-h-0 flex-1 overflow-hidden border-t border-border-default">
          <SessionList
            runs={runs}
            activeRunId={activeRunId}
            collapsed={!sidebarOpen}
            taskCounts={taskCounts}
            onSelect={loadSession}
            onDelete={deleteSession}
            onNewChat={newChat}
          />
        </div>
      )}

      <div className="mt-auto border-t border-border-default px-2 py-2">
        <a
          href={FEEDBACK_URL}
          target="_blank"
          rel="noopener noreferrer"
          title={sidebarOpen ? undefined : t("nav.feedback")}
          aria-label={sidebarOpen ? undefined : t("nav.feedback")}
          className={cn(
            "flex items-center gap-3 rounded-button px-3 py-2.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-secondary hover:text-text-primary",
            !sidebarOpen && "justify-center",
          )}
        >
          <Lightbulb className="h-5 w-5 shrink-0" />
          {sidebarOpen && <span>{t("nav.feedback")}</span>}
        </a>
      </div>

      {/* Resize handle — only when expanded */}
      {sidebarOpen && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t("sidebar.resize", "Resize sidebar")}
          aria-valuemin={SIDEBAR_LIMITS.MIN}
          aria-valuemax={SIDEBAR_LIMITS.MAX}
          aria-valuenow={sidebarWidth}
          tabIndex={0}
          onPointerDown={onPointerDown}
          onDoubleClick={onHandleDoubleClick}
          onKeyDown={onHandleKeyDown}
          className={cn(
            "group absolute top-0 right-0 z-10 h-full w-1.5 -mr-1 cursor-col-resize select-none touch-none",
            "before:absolute before:top-0 before:bottom-0 before:left-1/2 before:-translate-x-1/2 before:w-px before:bg-border-default",
            "hover:before:w-0.5 hover:before:bg-brand-500 focus:outline-none focus-visible:before:w-0.5 focus-visible:before:bg-brand-500",
            isDragging && "before:w-0.5 before:bg-brand-500",
          )}
        />
      )}
    </aside>
  );
}

export { Sidebar };
